import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { authorizedAxios, useAuth } from "@moonscape/shared";

/**
 * CRA Schedule 3 figures (`Exports.getCaSchedule3Data`). The net capital loss
 * fields are allowable (50%) amounts, null when the calc was not Canada ACB.
 */
export type CaSchedule3Totals = {
  disposals: number;
  proceeds: number;
  acb: number;
  outlays: number;
  gainLoss: number;
  taxableGainAfterLosses: number;
  netCapitalLoss: number | null;
  netCapitalLossApplied: number | null;
  netCapitalLossBalance: number | null;
  carryback: (number | null)[];
};

export type CaSchedule3Response = {
  taxYear: number;
  taxSetting: any;
  totals: CaSchedule3Totals;
  superficialLossDenials: number;
  caResidencyStart: string | null;
};

export const useCaSchedule3 = ({
  taxYear,
  enabled = true,
}: {
  taxYear: number;
  enabled?: boolean;
}): UseQueryResult<CaSchedule3Response | null, Error> => {
  const { getToken, user } = useAuth();

  const fetchSchedule3 = async (): Promise<CaSchedule3Response | null> => {
    const token = await getToken();
    try {
      const response = await authorizedAxios(token).get("/export/tax-report/ca/schedule-3", {
        params: { year: taxYear },
      });
      return response.data;
    } catch (error: any) {
      if (
        error?.response?.status === 400 &&
        error?.response?.data?.error === "unsupported_jurisdiction"
      ) {
        return null;
      }
      throw error;
    }
  };

  return useQuery({
    queryKey: ["ca_schedule_3", taxYear],
    queryFn: fetchSchedule3,
    enabled: enabled && !!user?.email && Number.isFinite(taxYear),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};
